import { IExternalStorageProvider } from "../driven/providers/externalStorageProvider";

export const AlertControllerFactory = ({
  alertService,
  externalStorageProvider,
}: {
  alertService: IAlertService;
  externalStorageProvider: IExternalStorageProvider;
}) => {
  const getAlertList: IGetAlertListController = async ({
    clinicId,
    selectedFloors,
    filter,
    paging,
    date,
    search,
    reload,
    pagination_flag,
  }) => {
    let payload: IGetAlertListPayload = {
      clinicId: clinicId,
      floorIds: selectedFloors,
      read: filter.read,
      unread: filter.unread,
      levels: filter.levels,
      page: paging.page + 1,
      page_size: paging.page_size,
      pagination_flag,
      consent: filter.consent,
    };

    let dateFilter = null;
    if (date) {
      dateFilter = { start: date.start, end: date.end };
    }

    if (dateFilter?.start) payload.start = dateFilter.start;
    if (dateFilter?.end) payload.end = dateFilter.end;
    if (search) payload.search = search;

    if (reload) {
      payload = { ...payload, page: 1 };
    }
    const response = await alertService.getAlertList(payload);
    return response;
  };

  const getAlertDetails: IGetAlertDetailController = async (payload) => {
    const alertDetails = await alertService.getAlertDetails(payload);
    if (alertDetails.success && alertDetails.data) {
      if (alertDetails.data?.graph_data_daily) {
        alertDetails.data.graph_data_daily = JSON.parse(
          String(alertDetails.data.graph_data_daily)
        );
      }
      if (alertDetails.data?.graph_data_hourly) {
        alertDetails.data.graph_data_hourly = JSON.parse(
          String(alertDetails.data.graph_data_hourly)
        );
      }

      if (alertDetails.data) {
        const fileURL = await externalStorageProvider.getFile({
          bucket: "resident-face-sheet",
          fileName: `public/clinicPatient/${alertDetails.data.uploaded_pdf_file_name}`,
        });
        alertDetails.data.file = await (await fetch(fileURL)).blob();
      }
      return alertDetails;
    } else {
      const response = await externalStorageProvider.getFile({
        bucket: "resident-face-sheet",
        fileName: `public/clinicPatient/${payload.file}`,
      });

      if (response) {
        return { success: true, data: response };
      }
    }
    return { success: false, error: { message: "Something went wrong!" } };
  };

  const getCombinedAlertList: IGetCombinedAlertListController = async ({
    clinicId,
    consent,
    escalationLevels,
    flagLevels,
    floorIds,
    page,
    page_size,
    reviewed,
    timestamp,
    reload,
  }) => {
    let payload: Parameters<IGetCombinedAlertListService>[0] = {
      clinic_id: clinicId,
      floor_ids: floorIds,
      escalation_levels: escalationLevels,
      flag_levels: flagLevels,
      reviewed: reviewed,
      timestamp: timestamp,
      consent: consent,
      page,
      page_size,
    };

    if (reload) {
      payload = { ...payload, page: 1 };
    }

    const response = await alertService.getCombinedAlertService(payload);
    return response;
  };

  const getFlagList: IGetFlagListController = async ({
    clinicId,
    startDate,
    endDate,
    floorIds,
    consent,
  }) => {
    const response = await alertService.getFlagListService({
      clinic_id: clinicId,
      start_dt: startDate,
      end_dt: endDate,
      floor_ids: floorIds,
      consent: consent,
    });
    return response;
  };

  const getInboxFlagsByReviewStatus: IGetInboxFlagsByReviewStatusController =
    async ({ clinicId, startDate, endDate, floorIds, consent, reviewed }) => {
      const response = await alertService.getInboxFlagsByReviewStatusService({
        clinic_id: clinicId,
        start_dt: startDate,
        end_dt: endDate,
        floor_ids: floorIds,
        consent: consent,
        reviewed: reviewed,
      });
      return response;
    };

  const getPatientFlags: IGetPatientFlagsController = async ({
    clinicId,
    patientId,
    page,
    pageSize,
    eventDate,
  }) => {
    const response = await alertService.getPatientFlagsService({
      clinic_id: clinicId,
      patient_id: patientId,
      page: page,
      page_size: pageSize,
      event_date: eventDate,
    });
    return response;
  };

  const getFlagAlertClinical: IGetFlagAlertClinicalController = async ({
    clinicId,
    startDate,
    endDate,
    floorIds,
    consent,
    patientIds,
  }) => {
    const payload: Parameters<IGetFlagAlertClinicalService>[0] = {
      clinic_id: clinicId,
      start_dt: startDate,
      end_dt: endDate,
      floor_ids: floorIds,
      consent: consent,
    };
    if (patientIds) {
      payload.patient_ids = patientIds;
    }
    const response = await alertService.getFlagAlertClinicalService(payload);

    return response;
  };

  const getPatientAlert: IGetPatientAlertController = async ({
    patientId,
    clinicId,
    timestamp,
  }) => {
    const payload: Parameters<IGetPatientAlertService>[0] = {
      patient_id: patientId,
      clinic_id: clinicId,
    };
    if (timestamp) {
      payload.timestamp = timestamp;
    }

    const response = await alertService.getPatientAlertService(payload);
    return response;
  };

  const addAlertAck: IAddAlertAck = async ({
    clinicId,
    escalationId,
    flaggedId,
    notes,
  }) => {
    const response = await alertService.addAlertAckService({
      clinic_id: clinicId,
      escalation_id: escalationId,
      flagged_id: flaggedId,
      notes,
    });
    return response;
  };

  const hideFlag: IHideFlag = async ({ clinicId, flaggedId }) => {
    const response = await alertService.hideFlagService({
      clinic_id: clinicId,
      flagged_id: flaggedId,
    });
    return response;
  };

  return {
    getAlertDetails,
    getAlertFile: alertService.getAlertFile,
    getAlertList,
    addAlertComment: alertService.addAlertComment,
    getCombinedAlertList,
    addAlertAck,
    getPatientAlert,
    getFlagList,
    getFlagAlertClinical,
    hideFlag,
    getInboxFlagsByReviewStatus,
    getPatientFlags,
  };
};
